import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import Modal from '@ui/modal/modal';
import { Activity, Dispatch, useState } from 'react';
import Papa from 'papaparse';
import CsvColumnMapperModal from './csv-column-mapper-modal';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import defaultPasscodeList from '@/micros/homepage/audiences/utils/defaultPasscodeList';
import { ImportedProfile, UploadPasscodeListMemberResponse, WorkingPasscodeList } from '@/types/passcode-list.types';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import UploadLarge from '@ui/uploads/upload-large';
import toastTrigger from '@ui/toast/toast-trigger';
import {
  GetUploadEndpoint,
  getUploadFilename,
  UploadFileToProvider,
  SetItemContentType,
} from '@ui/uploads/validations';
import ManualAddUserForm from './manual-add-user';
import { isValidEmail } from '../../utils';
import { CreateNoMemberPasscodeList, CreatePasscodeList } from '@/connection/audiences/passcode-lists';
import AddUsersToList from '@/connection/audiences/add-users-to-list';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchPasscodeLists } from '@/store/slices/passcode-lists';
import { fetchListAttendees } from '@/store/slices/audiences';
import { selectPasscodeLists } from '@/store/slices/passcode-lists/selectors';
import { getActiveChannel, getUser } from '@/store/slices/user/selectors';
import { AUDIENCE_CARD_TYPE, GateTypes, Requirement } from '@/types/registration.types';
import { getWorkingProjectSessions, selectWorkingProjectRegistrationSettings } from '@/store/slices/project/selectors';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import CloneLists from '@/connection/audiences/clone-lists';
import { useProjectUuidParam } from '@/utils/use-project-uuid-param';
import { EToastTypes } from '@ui/toast/toast.types';
import { RegFieldsEnum } from '@/types/registration.types';

type CreateListModalProps = {
  open: boolean;
  setOpen: Dispatch<React.SetStateAction<boolean>>;
  audienceCardType?: AUDIENCE_CARD_TYPE;
  passcodeListToClone?: number;
  onSaveComplete?: () => void;
  /** When provided the modal operates in update mode: skip name/description and add members to this list. */
  passcodeListId?: number;
};

const CreateListModal = ({
  open,
  setOpen,
  audienceCardType,
  passcodeListToClone,
  onSaveComplete,
  passcodeListId,
}: CreateListModalProps) => {
  const isUpdateMode = passcodeListId !== undefined;
  const dispatch = useAppDispatch();

  const user = useAppSelector(getUser);
  const passcodeLists = useAppSelector(selectPasscodeLists);
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const sessions = useAppSelector(getWorkingProjectSessions);
  const channel = useAppSelector(getActiveChannel);
  const projectUuidParam = useProjectUuidParam();

  const [workingPasscodeList, setWorkingPasscodeList] = useState<WorkingPasscodeList>(defaultPasscodeList);

  const [isUploadAddType, setIsUploadAddType] = useState(true);
  const [loading, setLoading] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [showColumnMapper, setShowColumnMapper] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);

  const handleClose = () => {
    setLoading(false);
    setWorkingPasscodeList(defaultPasscodeList);
    setUploadedFileName(null);
    setShowColumnMapper(false);
    setCsvHeaders([]);
    setOpen(false);
  };

  const handlePasscodeListName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWorkingPasscodeList((list: WorkingPasscodeList) => ({
      ...list,
      name: e.target.value,
    }));
  };

  const handlePasscodeListDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWorkingPasscodeList((list: WorkingPasscodeList) => ({
      ...list,
      description: e.target.value,
    }));
  };

  const handleAddTypeChange = () => {
    setIsUploadAddType(!isUploadAddType);
    setWorkingPasscodeList((list) => ({ ...list, url: '', manually_added_users: [] }));
    setUploadedFileName(null);
  };

  const hasInvalidManualEmails =
    workingPasscodeList.manually_added_users.length > 0 &&
    workingPasscodeList.manually_added_users.some((u) => !isValidEmail(u.email));

  const hasUsersToUpload =
    workingPasscodeList.url ||
    (workingPasscodeList.manually_added_users.length && !hasInvalidManualEmails) ||
    audienceCardType;

  const disableSave = isUpdateMode
    ? loading || !hasUsersToUpload
    : loading || !workingPasscodeList.name?.trim() || !hasUsersToUpload;

  const downloadErrorCSV = (newPasscodeList: UploadPasscodeListMemberResponse) => {
    if (newPasscodeList.csv) {
      const blob = new Blob([newPasscodeList['csv']], { type: 'text/csv;charset=utf-8' });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', 'magic-link-nonvalid-emails.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCloneRegsitrantList = async () => {
    if (!channel || !projectUuidParam || (!audienceCardType && !passcodeListToClone)) return;

    const registrantListToAdd = audienceCardType ? { [projectUuidParam]: [audienceCardType] } : null;

    const abortController = new AbortController();
    setLoading(true);
    try {
      const response = await CloneLists({
        channel,
        projectUuid: projectUuidParam,
        registrantListsToAdd: registrantListToAdd,
        passcodeListToClone: passcodeListToClone || null,
        listsToAdd: [],
        addListsToProject: false,
        name: workingPasscodeList.name,
        description: workingPasscodeList.description,
        signal: abortController.signal,
      });
      toastTrigger(response.message, {
        description: 'Registered, Attended and No shows may not be removed from their project.',
        type: EToastTypes.info,
        autoClose: 5000,
      });
    } catch (e) {
      console.error(e);

      toastTrigger('There was a problem cloning the user lists', {
        type: EToastTypes.info,
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePasscodeList = async () => {
    if (!user?.active_channel || !registrationSettings) return;

    const listToCreate = {
      ...workingPasscodeList,
      name: workingPasscodeList.name.trim(),
    };

    if (!listToCreate.name) {
      toastTrigger('Name Required', { type: 'error', autoClose: 5000 });
      return;
    }

    const lowerCaseName = listToCreate.name.toLowerCase();
    const isDuplicate = passcodeLists.some((list) => lowerCaseName === list.name?.toLowerCase());
    if (isDuplicate) {
      toastTrigger('Duplicate Audience List Name', {
        description: `"${listToCreate.name}" is already in use. Please enter a unique name.`,
        type: 'error',
        autoClose: 5000,
      });
      return;
    }

    setLoading(true);
    let newPasscodeList = null;

    try {
      if (listToCreate.url === '' && !listToCreate.manually_added_users.length) {
        newPasscodeList = await CreateNoMemberPasscodeList(user.active_channel, listToCreate);
      } else {
        newPasscodeList = await CreatePasscodeList(user.active_channel, listToCreate);
      }
    } catch {
      toastTrigger('Unable to save the list', {
        description: 'Failed to create new audience list',
        type: 'error',
        autoClose: 10000,
      });

      return handleClose();
    }

    if ('csv' in newPasscodeList) {
      toastTrigger('Unable to Create', {
        description: newPasscodeList.error || 'Failed to create new audience list',
        onClick: () => downloadErrorCSV(newPasscodeList),
        type: 'error',
        autoClose: 10000,
      });

      return handleClose();
    } else if ('error' in newPasscodeList) {
      toastTrigger('Unable to Create', {
        description: newPasscodeList.error || 'Failed to create new audience list',
        type: 'error',
        autoClose: 5000,
      });

      return handleClose();
    }

    if (Array.isArray(newPasscodeList.failedEmails) && newPasscodeList.failedEmails.length > 0) {
      toastTrigger('Some emails were invalid and failed to upload:', {
        description: newPasscodeList.failedEmails.join(' '),
        type: 'error',
        autoClose: 5000,
      });
    }

    dispatch(fetchPasscodeLists(user.active_channel));

    if (!newPasscodeList.passcode_list) {
      toastTrigger('Unable to Create', {
        description: 'Failed to create new audience list',
        type: 'error',
        autoClose: 5000,
      });

      return handleClose();
    }

    const sessionsIDs = sessions ? sessions.map((session) => session.session) : [];

    const workingGateRequirement: Requirement = {
      type: GateTypes.passcode_list,
      passcodeList: [newPasscodeList.passcode_list],
      gatedSessions: [...sessionsIDs],
    };

    const requirementsCopy = { ...registrationSettings?.requirements };
    const listsCopy = [...(requirementsCopy?.[GateTypes.passcode_list] || [])];

    listsCopy.push(workingGateRequirement);

    dispatch(
      saveWorkingProjectByFields({
        registration_settings: {
          ...registrationSettings,
          requirements: {
            ...registrationSettings.requirements,
            ...(listsCopy.length ? { [GateTypes.passcode_list]: listsCopy } : {}),
          },
        },
      })
    );

    handleClose();
  };

  const onFile = async (file: File | FileList) => {
    if (file instanceof File) {
      setLoading(true);
      try {
        const endpoint = await GetUploadEndpoint({
          filename: getUploadFilename(file.name),
        });

        const endpointObj = endpoint as { url?: string; fields?: Record<string, string>; cdnHost?: string };
        if (endpointObj?.url) {
          const finalUrl = await UploadFileToProvider(
            {
              url: endpointObj.url,
              fields: endpointObj.fields ?? {},
              cdnHost: endpointObj?.cdnHost,
            },
            file
          );
          SetItemContentType({ filename: finalUrl, contentType: file.type }).catch(console.error);
          setUploadedFileName(file.name);
          setWorkingPasscodeList((list: WorkingPasscodeList) => ({
            ...list,
            url: finalUrl,
          }));

          Papa.parse<string[]>(file, {
            preview: 1,
            header: false,
            complete: (results) => {
              const headers = (results.data[0] ?? []).filter(Boolean);
              setCsvHeaders(headers);
            },
            error: () => {
              toastTrigger('Error reading CSV columns', {
                description: 'Could not parse column headers from the uploaded file.',
                type: 'error',
              });
            },
          });
        }
      } catch (e) {
        console.error(e);
        toastTrigger('Error attaching file', {
          description: 'There was an error attaching the file.',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRemoveFile = () => {
    setWorkingPasscodeList((list) => ({ ...list, url: '' }));
    setUploadedFileName(null);
  };

  const handleAddManualUsersToList = async (listId: number) => {
    if (!channel || !projectUuidParam) return;
    const usersToAdd = workingPasscodeList.manually_added_users.map(
      (u) =>
        ({
          [RegFieldsEnum.first_name]: u.first_name,
          [RegFieldsEnum.last_name]: u.last_name,
          [RegFieldsEnum.email]: u.email,
        }) as ImportedProfile
    );
    setLoading(true);
    try {
      await AddUsersToList({ channel, projectUuid: projectUuidParam, passcodeLists: [listId], usersToAdd });
      toastTrigger('Users added successfully', { type: EToastTypes.success, autoClose: 5000 });
      dispatch(fetchListAttendees({ passcodeListId: listId }));
    } catch (e) {
      console.error(e);
      toastTrigger('There was a problem adding users to the list', { type: EToastTypes.error, autoClose: 5000 });
    } finally {
      handleClose();
    }
  };

  const handleNextOrSave = () => {
    if (isUploadAddType && !audienceCardType) {
      const listForMapper = isUpdateMode
        ? { ...workingPasscodeList, passcode_list: passcodeListId }
        : workingPasscodeList;
      setWorkingPasscodeList(listForMapper);
      setShowColumnMapper(true);
    } else if (!isUploadAddType && isUpdateMode && passcodeListId) {
      handleAddManualUsersToList(passcodeListId);
    } else if (audienceCardType) {
      handleCloneRegsitrantList();
    } else {
      handleCreatePasscodeList();
    }
  };

  return (
    <>
      <Modal
        open={open}
        onRequestClose={handleClose}
        customSize={{
          width: '640px',
          height: audienceCardType ? '240px' : 'auto',
          minHeight: audienceCardType ? '240px' : '480px',
          maxHeight: 'calc(100% - 32px)',
        }}
        animateHeightTransition
        contentWrapperClassName="padding-h-40 padding-v-24 flex-1 flex-column"
        backgroundColor="var(--gray-900)"
        RenderFooterContent={
          <Container background="var(--gray-900)" className="flex fd-row jc-flex-end ai-flex-end gap-16 margin-t-24">
            <Button
              onClick={handleClose}
              backgroundColor="var(--local-bttn-sec-default-bg)"
              hoverBackgroundColor="var(--local-bttn-sec-hover-bg)"
              className="L1 padding-v-16 padding-h-24"
            >
              Cancel
            </Button>
            <Button
              onClick={handleNextOrSave}
              className="padding-v-16 padding-h-24"
              disabledOpacity={0.6}
              backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
              disabled={disableSave}
            >
              <Text
                className="L1"
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              >
                {isUploadAddType && !audienceCardType ? 'Next' : 'Save'}
              </Text>
            </Button>
          </Container>
        }
      >
        <Container>
          <Activity mode={!isUpdateMode ? 'visible' : 'hidden'}>
            <InputWithOverlay
              typography="H4"
              isBorderless
              inputHeight="48px"
              status={nameTouched && workingPasscodeList.name.trim() === '' ? 'error' : 'default'}
              inputProps={{
                onBlur: () => setNameTouched(true),
                placeholder: 'Enter audience name',
                className: 'padding-0 radius-16',
                value: workingPasscodeList.name,
                onChange: handlePasscodeListName,
                maxLength: 100,
              }}
            />

            <InputWithOverlay
              typography="P1"
              isBorderless
              inputProps={{
                placeholder: 'Add description',
                className: 'height-24 padding-0',
                value: workingPasscodeList.description,
                onChange: handlePasscodeListDescription,
                maxLength: 100,
              }}
            />
          </Activity>

          <Activity mode={!audienceCardType ? 'visible' : 'hidden'}>
            <Container className="padding-t-28 padding-b-24">
              <Button
                clear
                borderRadius="100px"
                onClick={handleAddTypeChange}
                backgroundColor="var(--local-switch-bg)"
                hoverOpacity={0.8}
                className="flex ai-center jc-space-evenly gap-4 padding-2"
                width="100%"
                height="36px"
              >
                <Container
                  background={isUploadAddType ? 'var(--local-switch-active)' : undefined}
                  className="flex-column ai-center jc-center radius-100"
                  width="50%"
                  height="32px"
                >
                  <Text className="S2" design={isUploadAddType ? 'GlobalIcoText1' : 'GlobalIcoText3'}>
                    Upload list
                  </Text>
                </Container>
                <Container
                  background={!isUploadAddType ? 'var(--local-switch-active)' : undefined}
                  className="flex-column ai-center jc-center radius-100"
                  width="50%"
                  height="32px"
                >
                  <Text className="S2" design={!isUploadAddType ? 'GlobalIcoText1' : 'GlobalIcoText3'}>
                    Add manually
                  </Text>
                </Container>
              </Button>
            </Container>

            <Container>
              <Activity mode={isUploadAddType ? 'visible' : 'hidden'}>
                <Activity mode={uploadedFileName ? 'visible' : 'hidden'}>
                  <Container
                    height="64px"
                    className="flex fd-row ai-center jc-space-between radius-16"
                    design="GlobalCont2"
                  >
                    <Container className="flex fd-row ai-center gap-8 padding-16">
                      <Container design="GlobalCont3" className="padding-12 radius-8" height="40px">
                        <Icon
                          name={ICONS.ICON_FILE_DOCUMENT_PAGE_SCRIPT_TEXT_EMPTY}
                          size={16}
                          color="var(--gray-400)"
                        />
                      </Container>
                      <Text className="S1" design="GlobalIcoText2" wordBreak="break-all">
                        {uploadedFileName}
                      </Text>
                    </Container>
                    <Button onClick={handleRemoveFile}>
                      <Icon name={ICONS.DELETE_TRASH_BIN} size={12} />
                    </Button>
                  </Container>
                </Activity>

                <Activity mode={uploadedFileName ? 'hidden' : 'visible'}>
                  <Container height="88px">
                    <UploadLarge
                      onFile={onFile}
                      allowedFileTypes={['text/csv', 'application/vnd.ms-excel']}
                      style={{
                        border: '1px dashed var(--gray-600)',
                      }}
                      placeholder=""
                      maxSizeInBytes={5 * 1024 * 1024}
                      maxSizeHeaderMessage="Cannot Upload File"
                      maxSizeMessage="File size limit is 5 MB. Please break the file into multiple uploads."
                      CustomTitle={
                        <Container
                          className="flex fd-column jc-center ai-center margin-auto radius-10 overflow-hidden padding-8 gap-4"
                          width="fit-content"
                          minHeight="150px"
                          height="150px"
                          background="transparent"
                        >
                          <Text className="L2 flex fd-row jc-flex-start ai-center gap-8">
                            <Icon name={ICONS.ADD} size={16} color="var(--gray-500)" />
                            <Text className="S2" design="GlobalIcoText1">
                              Upload a list
                            </Text>
                          </Text>

                          <Container className="flex fd-row ai-center gap-8 padding-t-8">
                            <Text className="C2" fontSize="12px" design="GlobalIcoText3">
                              or drag and drop a .csv file here
                            </Text>
                          </Container>
                        </Container>
                      }
                    />
                  </Container>
                </Activity>
              </Activity>
              <Activity mode={isUploadAddType ? 'hidden' : 'visible'}>
                <Text className="S2">Profiles</Text>
                <ManualAddUserForm
                  onChange={(users) => setWorkingPasscodeList((list) => ({ ...list, manually_added_users: users }))}
                />
              </Activity>
            </Container>
          </Activity>
        </Container>
      </Modal>

      <CsvColumnMapperModal
        open={showColumnMapper}
        csvHeaders={csvHeaders}
        workingPasscodeList={workingPasscodeList}
        onClose={() => setShowColumnMapper(false)}
        onSaveComplete={() => {
          handleClose();
          onSaveComplete?.();
        }}
      />
    </>
  );
};

export default CreateListModal;
